import { useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Stack from '@mui/material/Stack'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Slide from '@mui/material/Slide'
import SpendingPower from 'components/CommonUI/SpendingPower'
import SessionTimeoutWarning from 'components/ModalViews/SessionTimeoutWarning'
import MobileNav from 'components/Header/MobileNav'
import Navigation from './Navigation'
import UserNav from './UserNav'
import Search from './Search'
import saga from 'sagas'
import { useInjectSaga } from 'utils/injectSaga'
import { setToken } from 'slices/globalSlice'
import { IS_BOOKING } from 'utils/constants'
import { useAction } from 'utils/actions'
import { useHeaderNavigation } from 'utils/hooks'
import { isEmpty } from 'lodash'
import { useAuth0 } from '@auth0/auth0-react'
import { ReactComponent as IconDondeLogo } from 'icons/DondeLogo.svg'
import useScrollTrigger from '@mui/material/useScrollTrigger'
import { useBreakpoints } from 'utils/hooks'

const HideOnScroll = (props) => {
  const trigger = useScrollTrigger()
  const { smDown } = useBreakpoints()

  return (
    <Slide 
      appear={false} 
      direction="down" 
      in={smDown ? !trigger : true}
    >
      {props.children}
    </Slide>
  )
}

const Header = ({
  hide,
}) => {
  useInjectSaga({ key: 'app', saga })
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useHeaderNavigation()
  const { 
    openModal, 
    getUser, 
    getCompany,
    getSavingsTotals,
  } = useAction()
  const timer = useRef(null)
  const { pathname } = location
  const {
    isLoading,
    isAuthenticated,
    // user: authUser,
    getAccessTokenSilently,
    logout,
    // loginWithPopup,
  } = useAuth0()
  const savingsTotals = useSelector(({ global }) => global.savingsTotals) || {}
  const accessToken = useSelector(({ global }) => global.token) || ''
  const user = useSelector(({ global }) => global.user)
  const company = useSelector(({ global }) => global.company)

  const openTimeout = useCallback(() => {
    openModal(<SessionTimeoutWarning />, { preventClose: true })
  }, [])

  const getToken = async cb => {
    if (isAuthenticated) {
      const {
        access_token,
        expires_in,
      } = await getAccessTokenSilently({ detailedResponse: true })
      dispatch(setToken({ token: access_token }))
      cb(expires_in)
      return access_token
    }
    if (!IS_BOOKING) {
      window.sessionStorage?.setItem('returnTo', location.pathname)
      logout({ returnTo: window.location.origin })
    }
  }

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      getToken(expires_in => {
        clearTimeout(timer.current)
        timer.current = setTimeout(openTimeout, expires_in * 1000)
      })
    }
    return () => {
      clearTimeout(timer.current)
    }
  }, [pathname, isLoading, isAuthenticated])

  useEffect(() => {
    if (isAuthenticated && !user?.unique_id && !!accessToken?.length) {
      getUser()
    }
    if (
      !!accessToken?.length &&
      isEmpty(savingsTotals) &&
      user?.unique_id
    ) {
      getSavingsTotals()
    }
    if (
      !!accessToken?.length &&
      isEmpty(company) &&
      user?.unique_id
    ) getCompany()
  }, [isAuthenticated, accessToken, user?.unique_id, user?.has_savings_account])

  useEffect(() => {
    const donde_tdata = document.getElementById('donde_tdata')?.value
    if (donde_tdata) {
      dispatch(setToken({ token: donde_tdata }))
      getUser()
      getSavingsTotals()
    }
  }, [])

  if (hide || !!!user?.unique_id) return null

  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="sticky" 
          sx={{ 
            bgcolor: 'background.paper.main',
            color: 'background.paper.text',
            top: 0, 
            bottom: 'auto', 
            height: {
              xx: 70,
              md: 90,
            },
            zIndex: 'header',
            boxShadow: '0 5px 10px 0 rgba(42, 115, 156, .05)',
          }}
        >
          <Toolbar
            sx={{
              display: 'grid',
              gap: {
                xx: 2,
                md: 3,
              },
              gridTemplateColumns: {
                xx: 'auto 1fr',
                md: 'auto 1fr auto',
              },
              height: 'inherit',
            }}
          >
            <Box
              alignSelf="center"
              tabindex="0"
              role="button"
              sx={{
                width: {
                  xx: 20,
                  sm: 'initial',
                },
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              <IconDondeLogo
                viewBox="0 0 68 19"
                width="105"
                height="55"
              />
            </Box>
            <Navigation />
            <Stack
              sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'flex-end', })}
            >
              {/* <Search /> */}
              <SpendingPower />
              <UserNav />
            </Stack>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <MobileNav />
    </>
  )
}

export default Header